import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('mj_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Restaurant
  getRestaurant: () => axios.get(`${API}/restaurant`).then(r => r.data),
  updateRestaurant: (data) => axios.put(`${API}/restaurant`, data, { headers: getAuthHeader() }).then(r => r.data),
  setPauseIncomingOrders: (paused) => axios.put(
    `${API}/restaurant/pause-orders`,
    { paused },
    { headers: getAuthHeader() }
  ).then(r => r.data),

  // Categories
  getCategories: () => axios.get(`${API}/categories`).then(r => r.data),
  createCategory: (data) => axios.post(`${API}/categories`, data, { headers: getAuthHeader() }).then(r => r.data),
  updateCategory: (id, data) => axios.put(`${API}/categories/${id}`, data, { headers: getAuthHeader() }).then(r => r.data),
  deleteCategory: (id) => axios.delete(`${API}/categories/${id}`, { headers: getAuthHeader() }).then(r => r.data),

  // Menu Items
  getMenuItems: (categoryId) => {
    const url = categoryId ? `${API}/menu-items?categoryId=${categoryId}` : `${API}/menu-items`;
    return axios.get(url).then(r => r.data);
  },
  createMenuItem: (data) => axios.post(`${API}/menu-items`, data, { headers: getAuthHeader() }).then(r => r.data),
  updateMenuItem: (id, data) => axios.put(`${API}/menu-items/${id}`, data, { headers: getAuthHeader() }).then(r => r.data),
  deleteMenuItem: (id) => axios.delete(`${API}/menu-items/${id}`, { headers: getAuthHeader() }).then(r => r.data),

  // Auth
  register: (data) => axios.post(`${API}/auth/register`, data).then(r => r.data),
  verifyEmail: (token) => axios.get(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`).then(r => r.data),
  resendVerification: (data) => axios.post(`${API}/auth/resend-verification`, data),
  forgotPassword: (data) => axios.post(`${API}/auth/forgot-password`, data).then(r => r.data),
  resetPassword: (data) => axios.post(`${API}/auth/reset-password`, data).then(r => r.data),
  login: (data) => axios.post(`${API}/auth/login`, data).then(r => r.data),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getAuthHeader() }).then(r => r.data),

  // Coupons
  validateCoupon: (code, subtotal, items = []) => axios.post(`${API}/coupons/validate`, { code, subtotal, items }).then(r => r.data),
  getCoupons: () => axios.get(`${API}/coupons`, { headers: getAuthHeader() }).then(r => r.data),
  createCoupon: (data) => axios.post(`${API}/coupons`, data, { headers: getAuthHeader() }).then(r => r.data),
  updateCouponStatus: (id, active) => axios.put(`${API}/coupons/${id}/status`, { active }, { headers: getAuthHeader() }).then(r => r.data),
  deleteCoupon: (id) => axios.delete(`${API}/coupons/${id}`, { headers: getAuthHeader() }).then(r => r.data),

  // Orders
  createOrder: (data) => axios.post(`${API}/orders`, data, { headers: getAuthHeader() }).then(r => r.data),
  getOrders: () => axios.get(`${API}/orders`, { headers: getAuthHeader() }).then(r => r.data),
  getOrder: (id) => axios.get(`${API}/orders/${id}`, { headers: getAuthHeader() }).then(r => r.data),
  getAllOrders: () => axios.get(`${API}/orders/all`, { headers: getAuthHeader() }).then(r => r.data),
  getAdminOrders: (params = {}, options = {}) => axios.get(`${API}/admin/orders`, { params, headers: getAuthHeader(), signal: options.signal }).then(r => r.data),
  getAdminOrderSummary: () => axios.get(`${API}/admin/orders/summary`, { headers: getAuthHeader() }).then(r => r.data),
  getAdminDashboard: (params = {}) => axios.get(`${API}/admin/dashboard`, { params, headers: getAuthHeader() }).then(r => r.data),
  updateOrderStatus: (id, status) => axios.put(`${API}/orders/${id}/status`, { status }, { headers: getAuthHeader() }).then(r => r.data),

  // Payments
  createPaymentIntent: (orderId, amount) => axios.post(`${API}/payments/create-intent`, { orderId, amount }, { headers: getAuthHeader() }).then(r => r.data),
  confirmPayment: (orderId, paymentId) => axios.post(`${API}/payments/confirm`, { orderId, paymentId }, { headers: getAuthHeader() }).then(r => r.data),
  createCheckoutSession: (orderId) => axios.post(`${API}/payments/create-checkout-session`, { orderId }, { headers: getAuthHeader() }).then(r => r.data),
  cancelCheckoutSession: (orderId, cancelToken) => axios.post(
    `${API}/payments/cancel-checkout-session`,
    { orderId, cancelToken },
    { headers: getAuthHeader() }
  ).then(r => r.data),
  markPaymentSuccess: (orderId) => axios.get(`${API}/payments/success/${orderId}`, { headers: getAuthHeader() }).then(r => r.data),
  refundOrder: (orderId) => axios.post(`${API}/admin/orders/${orderId}/refund`, {}, { headers: getAuthHeader() }).then(r => r.data),
  getAdminPayouts: (params = {}) => axios.get(`${API}/admin/payouts`, { params, headers: getAuthHeader() }).then(r => r.data),
  getAdminPayoutTransactions: (payoutId, params = {}) => axios.get(`${API}/admin/payouts/${encodeURIComponent(payoutId)}/transactions`, { params, headers: getAuthHeader() }).then(r => r.data),

  // Option Sets
  getOptionSets: () => axios.get(`${API}/option-sets`, { headers: getAuthHeader() }).then(r => r.data),
  createOptionSet: (data) => axios.post(`${API}/option-sets`, data, { headers: getAuthHeader() }).then(r => r.data),
  updateOptionSet: (id, data) => axios.put(`${API}/option-sets/${id}`, data, { headers: getAuthHeader() }).then(r => r.data),
  deleteOptionSet: (id) => axios.delete(`${API}/option-sets/${id}`, { headers: getAuthHeader() }).then(r => r.data),

  // Delivery Zones
  getDeliveryZones: () => axios.get(`${API}/delivery-zones`).then(r => r.data),
  createDeliveryZone: (data) => axios.post(`${API}/delivery-zones`, data, { headers: getAuthHeader() }).then(r => r.data),
  updateDeliveryZone: (id, data) => axios.put(`${API}/delivery-zones/${id}`, data, { headers: getAuthHeader() }).then(r => r.data),
  deleteDeliveryZone: (id) => axios.delete(`${API}/delivery-zones/${id}`, { headers: getAuthHeader() }).then(r => r.data),
  checkDeliveryZone: (postcode) => axios.post(`${API}/delivery-zones/check`, { postcode }).then(r => r.data),

  // Postcode lookup
  lookupPostcode: (postcode) => axios.get(`${API}/postcode/${encodeURIComponent(postcode.replace(/\s+/g,''))}/addresses`).then(r => r.data),

  // Customers
  getCustomers: () => axios.get(`${API}/customers`, { headers: getAuthHeader() }).then(r => r.data),
  getCustomer: (id) => axios.get(`${API}/customers/${id}`, { headers: getAuthHeader() }).then(r => r.data),
  getAdminCustomers: (params = {}) => axios.get(`${API}/admin/customers`, { params, headers: getAuthHeader() }).then(r => r.data),
  getAdminCustomer: (id, params = {}) => axios.get(`${API}/admin/customers/${id}`, { params, headers: getAuthHeader() }).then(r => r.data),

  // Reviews
  getReviews: () => axios.get(`${API}/reviews`).then(r => r.data),
  getReviewsStats: () => axios.get(`${API}/reviews/stats`).then(r => r.data),
  createReview: (data) => axios.post(`${API}/reviews`, data).then(r => r.data),
  deleteReview: (id) => axios.delete(`${API}/reviews/${id}`, { headers: getAuthHeader() }).then(r => r.data),
  validateReviewToken: (token) => axios.get(`${API}/reviews/validate-token/${token}`).then(r => r.data),
  submitReviewWithToken: (data) => axios.post(`${API}/reviews/submit-token`, data).then(r => r.data),

  // Coupons
  checkActiveBogo: () => axios.get(`${API}/coupons/active-bogo`).then(r => r.data),

  // User Profile
  updateProfile: (data) => axios.put(`${API}/auth/profile`, data, { headers: getAuthHeader() }).then(r => r.data),

  // Static Pages
  getStaticPages: () => axios.get(`${API}/static-pages`).then(r => r.data),
  getStaticPage: (slug) => axios.get(`${API}/static-pages/${slug}`).then(r => r.data),
  updateStaticPage: (slug, data) => axios.put(`${API}/static-pages/${slug}`, data, { headers: getAuthHeader() }).then(r => r.data),

  // Rewards - customer
getPublicRewards: () =>
  axios.get(`${API}/rewards`).then(r => r.data),

requestRewardsOtp: (email) =>
  axios.post(`${API}/rewards/request-otp`, { email }).then(r => r.data),

verifyRewardsOtp: (email, code) =>
  axios.post(`${API}/rewards/verify`, { email, code }).then(r => r.data),

// Rewards - admin
getAdminRewards: () =>
  axios.get(`${API}/rewards/admin/rewards`, {
    headers: getAuthHeader()
  }).then(r => r.data),

createAdminReward: (data) =>
  axios.post(`${API}/rewards/admin/rewards`, data, {
    headers: getAuthHeader()
  }).then(r => r.data),

updateAdminReward: (id, data) =>
  axios.put(`${API}/rewards/admin/rewards/${id}`, data, {
    headers: getAuthHeader()
  }).then(r => r.data),

deleteAdminReward: (id) =>
  axios.delete(`${API}/rewards/admin/rewards/${id}`, {
    headers: getAuthHeader()
  }).then(r => r.data),

redeemReward: (data) =>
  axios.post("/api/rewards/redeem", data).then(r => r.data),

// Promotions - public
getPromotions: () =>
  axios.get(`${API}/promotions`).then(r => r.data),

// Promotions - admin
getAdminPromotions: () =>
  axios.get(`${API}/admin/promotions`, {
    headers: getAuthHeader()
  }).then(r => r.data),

createPromotion: (data) =>
  axios.post(`${API}/admin/promotions`, data, {
    headers: getAuthHeader()
  }).then(r => r.data),

updatePromotion: (id, data) =>
  axios.put(`${API}/admin/promotions/${id}`, data, {
    headers: getAuthHeader()
  }).then(r => r.data),

deletePromotion: (id) =>
  axios.delete(`${API}/admin/promotions/${id}`, {
    headers: getAuthHeader()
  }).then(r => r.data),

};
